import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Plus, Zap, Cpu, Sun, ShieldCheck, AlertTriangle, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'p1_meter', label: 'P1 / slimme meter' },
  { id: 'inverter', label: 'Omvormer / zonnepanelen' },
  { id: 'solar', label: 'Zonnepanelen' },
  { id: 'home_battery', label: 'Thuisbatterij' },
  { id: 'ev_charger', label: 'Laadpaal' },
  { id: 'heat_pump', label: 'Warmtepomp' },
  { id: 'airco', label: 'Airco' },
  { id: 'boiler', label: 'Boiler' },
  { id: 'smart_plug', label: 'Slimme stekker' },
  { id: 'thermostat', label: 'Thermostaat' },
  { id: 'hub', label: 'Homey / Home Assistant / hub' },
  { id: 'unknown_other', label: 'Anders' },
];

const STATUS_LABEL = {
  manual_added: 'Handmatig toegevoegd',
  detected_by_qr: 'Herkend via QR',
  read_only_connected: 'Read-only verbonden',
  limited_control_available: 'Beperkte sturing mogelijk',
  control_available: 'Sturing mogelijk',
  installer_required: 'Installateur nodig',
  incompatible: 'Niet compatibel',
  unsupported: 'Niet ondersteund',
  error: 'Fout',
};

const STATUS_BADGE = {
  manual_added: 'bg-slate-100 text-slate-700',
  detected_by_qr: 'bg-indigo-100 text-indigo-800',
  read_only_connected: 'bg-emerald-100 text-emerald-800',
  limited_control_available: 'bg-amber-100 text-amber-800',
  control_available: 'bg-amber-100 text-amber-800',
  installer_required: 'bg-orange-100 text-orange-800',
  incompatible: 'bg-rose-100 text-rose-800',
  unsupported: 'bg-rose-100 text-rose-800',
  error: 'bg-rose-100 text-rose-800',
};

function DeviceCard({ device, onTest, busy }) {
  return (
    <div className="rounded-xl border border-border bg-white p-4 space-y-2" data-testid={`device-card-${device.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-medium text-sm">{device.display_name}</div>
          <div className="text-[11px] text-muted-foreground">
            {device.brand} {device.model} <span className="font-mono">[{device.category}]</span>
          </div>
        </div>
        <span className={`text-[10px] uppercase rounded-full px-2 py-0.5 ${STATUS_BADGE[device.status] || ''}`}>
          {STATUS_LABEL[device.status] || device.status}
        </span>
      </div>
      {device.confidence_score > 0 && (
        <div className="text-[11px] text-muted-foreground">
          Herkenning: {(device.confidence_score * 100).toFixed(0)}%
          {device.pending_validation && <span className="ml-2 text-amber-700">• nog te bevestigen</span>}
        </div>
      )}
      {(device.capabilities || []).length > 0 && (
        <div className="text-[11px] text-muted-foreground">
          Wat we kunnen uitlezen: {device.capabilities.map(c => c.replace('can_read_', '').replace(/_/g, ' ')).join(', ')}
        </div>
      )}
      {device.category === 'p1_meter' && device.config?.host && (
        <button onClick={() => onTest(device)} disabled={busy} className="text-[11px] text-primary underline disabled:opacity-50" data-testid={`device-test-${device.id}`}>
          Verbinding testen
        </button>
      )}
    </div>
  );
}

function AddDeviceModal({ open, onClose, pilotEmail, onAdded }) {
  const [category, setCategory] = useState('smart_plug');
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [qrText, setQrText] = useState('');
  const [host, setHost] = useState('');
  const [busy, setBusy] = useState(false);
  const [match, setMatch] = useState(null);

  const onLookup = async () => {
    if (!brand && !qrText) return;
    try {
      const r = await axios.get(`${API}/devices/known-models`, {
        params: { q: brand || qrText, category },
      });
      if (r.data.items?.length > 0) {
        setMatch(r.data.items[0]);
        if (!model) setModel(r.data.items[0].model);
      } else {
        setMatch(null);
      }
    } catch (err) { /* silent */ }
  };

  const onSubmit = async () => {
    if (!category) return;
    try {
      setBusy(true);
      const payload = {
        pilot_email: pilotEmail,
        category,
        brand: brand.trim().toLowerCase(),
        model: model.trim().toLowerCase(),
        display_name: displayName.trim(),
        qr_text: qrText.trim(),
        config: host ? { host } : null,
      };
      const r = await axios.post(`${API}/devices`, payload);
      toast.success('Apparaat toegevoegd');
      onAdded(r.data);
      onClose();
      setBrand(''); setModel(''); setDisplayName(''); setQrText(''); setHost(''); setMatch(null);
    } catch (err) {
      toast.error(`Toevoegen mislukt: ${err?.response?.data?.detail || err.message}`);
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="add-device-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-light">Apparaat koppelen</h3>
          <button onClick={onClose} className="text-muted-foreground" data-testid="add-device-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-2 text-sm">
          <label className="block">
            <div className="text-xs text-muted-foreground mb-1">Categorie</div>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full border border-border rounded px-2 py-1.5 text-sm" data-testid="add-device-category">
              {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-xs text-muted-foreground mb-1">QR-tekst (optioneel — plak code/label-tekst)</div>
            <input value={qrText} onChange={(e) => setQrText(e.target.value)} onBlur={onLookup} placeholder='bijv. "altherma" of "HWE-P1"' className="w-full border border-border rounded px-2 py-1.5 text-sm" data-testid="add-device-qr" />
          </label>
          <label className="block">
            <div className="text-xs text-muted-foreground mb-1">Merk</div>
            <input value={brand} onChange={(e) => setBrand(e.target.value)} onBlur={onLookup} placeholder="bijv. shelly, homewizard" className="w-full border border-border rounded px-2 py-1.5 text-sm" data-testid="add-device-brand" />
          </label>
          <label className="block">
            <div className="text-xs text-muted-foreground mb-1">Model</div>
            <input value={model} onChange={(e) => setModel(e.target.value)} placeholder="bijv. plug-s-gen3" className="w-full border border-border rounded px-2 py-1.5 text-sm" data-testid="add-device-model" />
          </label>
          <label className="block">
            <div className="text-xs text-muted-foreground mb-1">Eigen naam (optioneel)</div>
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="bijv. Bureaulamp" className="w-full border border-border rounded px-2 py-1.5 text-sm" data-testid="add-device-name" />
          </label>
          {category === 'p1_meter' && (
            <label className="block">
              <div className="text-xs text-muted-foreground mb-1">Lokaal IP/hostname (alleen P1)</div>
              <input value={host} onChange={(e) => setHost(e.target.value)} placeholder="bijv. 192.168.1.50" className="w-full border border-border rounded px-2 py-1.5 text-sm" data-testid="add-device-host" />
            </label>
          )}
          {match && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2 text-[11px]" data-testid="add-device-match">
              <strong>We denken dit te herkennen:</strong> {match.brand} {match.model} ({match.category}) — slimme-stekker: <code>{match.smart_plug_suitable}</code>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg bg-slate-100 text-slate-700">Annuleren</button>
          <button onClick={onSubmit} disabled={busy || !category} className="px-3 py-1.5 text-sm rounded-lg bg-primary text-white disabled:opacity-50" data-testid="add-device-submit">
            {busy ? 'Toevoegen...' : 'Toevoegen'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MyDevicesPage() {
  const [params] = useSearchParams();
  const pilotEmail = params.get('email');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [devices, setDevices] = useState({ items: [], pilot_label: '' });
  const [salderen, setSalderen] = useState(null);
  const [tariff, setTariff] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const fetchAll = useCallback(async () => {
    if (!pilotEmail) {
      setErrorMsg('Geen pilot email. Open deze pagina via uw uitnodigingslink: /my-devices?email=...');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const [d, s, t] = await Promise.all([
        axios.get(`${API}/devices`, { params: { pilot_email: pilotEmail } }),
        axios.get(`${API}/insight/salderen-2027`),
        axios.get(`${API}/insight/tariff-today`),
      ]);
      setDevices(d.data);
      setSalderen(s.data);
      setTariff(t.data);
      setErrorMsg('');
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || err.message);
    } finally { setLoading(false); }
  }, [pilotEmail]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const onTest = async (device) => {
    try {
      setBusy(true);
      const r = await axios.post(`${API}/devices/${device.id}/test-connection?pilot_email=${encodeURIComponent(pilotEmail)}`);
      if (r.data.success || r.data.status === 'ok') {
        toast.success('Verbonden!');
      } else {
        toast.warning(`Verbinding mislukt: ${r.data.message || r.data.reason}`);
      }
      await fetchAll();
    } catch (err) {
      toast.error(`Test mislukt: ${err?.response?.data?.detail || err.message}`);
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (errorMsg) {
    const isNoEmail = errorMsg.includes('Geen pilot email') || errorMsg.includes('uitnodigingslink');
    const isNoAccess = errorMsg === 'no_access' || errorMsg === 'not_a_pilot' || errorMsg === 'pilot_inactive';
    if (isNoEmail || isNoAccess) {
      const headline = isNoEmail
        ? 'Apparaten-omgeving'
        : 'Geen actief Energy Control abonnement';
      const body = isNoEmail
        ? 'De apparaten-omgeving wordt persoonlijk klaargezet op basis van uw e-mailadres. Heeft u Energy Control net gestart? Dan ontvangt u binnenkort een welkomstmail met een persoonlijke uitnodigingslink.'
        : 'Op dit e-mailadres is nog geen actief Energy Control abonnement gevonden. Start Energy Control voor € 9,95 per maand de eerste 3 maanden, daarna € 14,95 per maand (maandelijks opzegbaar) om uw persoonlijke apparaten-omgeving te activeren.';
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-primary/5 to-white p-6">
          <div
            className="bg-white border border-border rounded-2xl shadow-lg max-w-lg w-full p-8 text-center space-y-4"
            data-testid="my-devices-onboarding"
          >
            <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
              <Cpu className="w-9 h-9 text-primary" />
            </div>
            <h1 className="text-2xl font-semibold text-foreground">{headline}</h1>
            <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
            <div className="space-y-2 pt-2">
              <a
                href="/energy-control-intake"
                className="w-full inline-flex items-center justify-center gap-1 px-4 py-3 rounded-md bg-primary text-white text-sm font-semibold hover:bg-primary/90"
                data-testid="my-devices-cta-intake"
              >
                Bekijk mijn demo / start Energy Control
              </a>
              <a
                href="/"
                className="w-full inline-flex items-center justify-center gap-1 px-4 py-3 rounded-md border border-border text-foreground text-sm font-semibold hover:bg-muted"
                data-testid="my-devices-cta-home"
              >
                Terug naar home
              </a>
            </div>
            <p className="text-[11px] text-muted-foreground pt-2">
              Vragen? Stuur ons een mail via <a href="/contact" className="underline">contact</a>.
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md text-center space-y-3">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
          <p className="text-sm text-muted-foreground" data-testid="error-message">{errorMsg}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="my-devices-page">
      <header className="border-b border-border bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-primary" />
          <span className="text-lg font-light text-primary" style={{ fontFamily: 'Outfit' }}>Powersure — Mijn apparaten</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-6 space-y-6">
        {/* Pilot label */}
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900 flex items-start gap-2" data-testid="pilot-label">
          <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{devices.pilot_label || 'Read-only / limited-control pilot · meten, herkennen en advies · automatische sturing alleen waar veilig ondersteund.'}</span>
        </div>

        {/* Devices */}
        <section data-testid="devices-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-light flex items-center gap-2"><Cpu className="w-5 h-5" /> Uw apparaten ({devices.items.length})</h2>
            <button onClick={() => setShowAdd(true)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary text-white text-sm" data-testid="add-device-btn">
              <Plus className="w-4 h-4" /> Apparaat koppelen
            </button>
          </div>
          {devices.items.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              Nog geen apparaten. Klik op <strong>Apparaat koppelen</strong> om te beginnen.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              {devices.items.map(d => <DeviceCard key={d.id} device={d} onTest={onTest} busy={busy} />)}
            </div>
          )}
        </section>

        {/* Salderen 2027 */}
        {salderen && (
          <section className="rounded-2xl border border-border bg-white p-5 space-y-3" data-testid="salderen-section">
            <div className="flex items-center gap-2">
              <Sun className="w-5 h-5 text-amber-500" />
              <h2 className="text-lg font-light">{salderen.headline}</h2>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">{salderen.intro}</p>
            <div className="grid md:grid-cols-2 gap-3 text-xs">
              <div>
                <div className="font-medium text-emerald-700 mb-1">Wat doet Powersure</div>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  {salderen.what_powersure_does.map((t, i) => <li key={i}>{t}</li>)}
                </ul>
              </div>
              <div>
                <div className="font-medium text-rose-700 mb-1">Wat doet Powersure NIET</div>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  {salderen.what_powersure_does_NOT.map((t, i) => <li key={i}>{t}</li>)}
                </ul>
              </div>
            </div>
            <div className="text-[10px] text-muted-foreground">Bron: {salderen.official_source}</div>
          </section>
        )}

        {/* Tarief vandaag */}
        {tariff && (
          <section className="rounded-2xl border border-border bg-white p-5 space-y-3" data-testid="tariff-section">
            <h2 className="text-lg font-light">Tarief & Timing — vandaag</h2>
            <div className="text-[11px] text-amber-700">{tariff.note}</div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-emerald-50 rounded-lg p-3">
                <div className="text-[10px] uppercase text-emerald-700">Goedkoopste uur</div>
                <div className="font-mono text-lg">{String(tariff.cheapest_hour.hour).padStart(2, '0')}:00</div>
                <div className="text-[11px] text-muted-foreground">€{tariff.cheapest_hour.price_eur_per_kwh.toFixed(3)} / kWh</div>
              </div>
              <div className="bg-rose-50 rounded-lg p-3">
                <div className="text-[10px] uppercase text-rose-700">Duurste uur</div>
                <div className="font-mono text-lg">{String(tariff.most_expensive_hour.hour).padStart(2, '0')}:00</div>
                <div className="text-[11px] text-muted-foreground">€{tariff.most_expensive_hour.price_eur_per_kwh.toFixed(3)} / kWh</div>
              </div>
            </div>
            {/* Mini curve */}
            <div className="flex items-end gap-1 h-16">
              {tariff.curve.map((c) => {
                const max = tariff.most_expensive_hour.price_eur_per_kwh;
                const h = (c.price_eur_per_kwh / max) * 100;
                const color = c.tier === 'low' ? 'bg-emerald-400' : c.tier === 'high' ? 'bg-rose-400' : 'bg-slate-300';
                return <div key={c.hour} title={`${c.hour}:00 €${c.price_eur_per_kwh}`} style={{ height: `${h}%` }} className={`flex-1 ${color} rounded-t`} />;
              })}
            </div>
            <ul className="text-xs text-muted-foreground space-y-1">
              {tariff.advice.map((a, i) => <li key={i}>• {a}</li>)}
            </ul>
            <div className="text-[10px] text-muted-foreground italic">
              {tariff.hard_rules.join(' · ')}
            </div>
          </section>
        )}
      </main>

      <AddDeviceModal open={showAdd} onClose={() => setShowAdd(false)} pilotEmail={pilotEmail} onAdded={fetchAll} />
    </div>
  );
}
